import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {
  StudentTicketContactDataDto,
  StudentTicketListViewAsmResponseDTO
} from '../../../models/student-ticket/list-view/StudentTicketListViewAsmResponseDTO';
import {AddressDataInfoDto} from '../../../models/student-ticket/list-view/AddressDataInfoDto';
import {WizardStep, WizardStepId, WIZARD_STEPS} from './wizard-step.model';
import {WizardStepShellComponent} from './wizard-step-shell/wizard-step-shell.component';
import {WizardStepComponent} from './wizard-step-component.interface';
import {
  VerificationWizardStorageService
} from '../../../@core/services/verification-wizard/verification-wizard-storage.service';
import {NameStepComponent, NameStepData} from './steps/name-step/name-step.component';
import {AddressStepComponent, AddressStepData} from './steps/address-step/address-step.component';
import {EmailStepComponent, EmailStepData} from './steps/email-step/email-step.component';
import {SchoolStepComponent} from './steps/school-step/school-step.component';
import {GeschwisterStepComponent, GeschwisterStepData} from './steps/geschwister-step/geschwister-step.component';
import {PaymentStepComponent} from './steps/payment-step/payment-step.component';
import {SummaryStepComponent} from './steps/summary-step/summary-step.component';
import {SchoolDataInfoDto} from '../../../models/student-ticket/list-view/SchoolDataInfoDto';
import {StudentTicketMandateHolderDto} from '../../../models/student-ticket/payment/StudentTicketMandateHolderDto';
import {StudentTicketService} from '../../../@core/services/student-ticket/student-ticket.service';
import {
  StudentTicketBatchUpdateRequestDto
} from '../../../models/student-ticket/verification-wizard/StudentTicketBatchUpdateRequestDto';
import {CustomToastService} from '../../../@core/utils/custom-toast.service';
import {isDeepEqual} from '../../../@core/utils/forms/formvalue-utils';
import {TranslateService} from '@ngx-translate/core';

type DiffableStep = {
  id: WizardStepId;
  field: keyof StudentTicketBatchUpdateRequestDto;
  applyIfChanged: (ticketId: string, request: StudentTicketBatchUpdateRequestDto) => Promise<boolean>;
};

/**
 * Mehrstufiger Ticket-Überprüfungs-Wizard.
 * Führt den Antragsteller durch Name, Adresse, Email, Schule, Geschwister und
 * Zahlungsdaten und zeigt abschließend eine Übersicht zur Bestätigung.
 * Die Übermittlung erfolgt gesammelt über den Batch-Update-Endpunkt, der alle
 * geänderten Abschnitte in einer Transaktion aktualisiert und das Ticket verifiziert.
 */
@Component({
  selector: 'app-verification-wizard',
  standalone: true,
  templateUrl: './verification-wizard.component.html',
  imports: [
    WizardStepShellComponent,
    NameStepComponent,
    AddressStepComponent,
    EmailStepComponent,
    SchoolStepComponent,
    GeschwisterStepComponent,
    PaymentStepComponent,
    SummaryStepComponent,
  ],
})
export class VerificationWizardComponent implements OnInit {

  @Input() ticket: StudentTicketListViewAsmResponseDTO;
  @Input() addressDataChangeEnabled = true;
  @Input() schoolDataChangeEnabled = true;
  @Input() geschwisterEigenanteilKomponenteEnabled = true;

  @ViewChild('stepRef') private stepRef?: WizardStepComponent;

  protected readonly activeModal = inject(NgbActiveModal);
  private readonly storageService = inject(VerificationWizardStorageService);
  private readonly studentTicketService = inject(StudentTicketService);
  private readonly customToastService = inject(CustomToastService);
  private readonly translateService = inject(TranslateService);

  protected steps: WizardStep[] = WIZARD_STEPS;
  protected currentStepIndex = 0;
  protected maxReachedIndex = 0;
  protected submitting = false;
  // wird erst true, sobald die (verschlüsselt persistierten) Step-Daten geladen sind - vorher
  // würde das Template mit unvollständigen initialData-Bindings rendern.
  protected dataLoaded = false;

  protected nameStepData: NameStepData;
  protected addressStepData: AddressStepData;
  protected emailStepData: EmailStepData;
  protected schoolStepData: SchoolDataInfoDto;
  protected geschwisterStepData: GeschwisterStepData;
  protected paymentStepData: StudentTicketMandateHolderDto | null;

  // Steps für Felder, deren Bearbeitung per Feature-Toggle deaktiviert werden kann - fehlen sie
  // im Wizard komplett, statt nur schreibgeschützt angezeigt zu werden (Adresse/Schule stehen
  // dann nur noch im Ticket).
  private readonly toggleableSteps: Readonly<Partial<Record<WizardStepId, () => boolean>>> = {
    [WizardStepId.ADDRESS]: () => this.addressDataChangeEnabled,
    [WizardStepId.SCHOOL]: () => this.schoolDataChangeEnabled,
    [WizardStepId.GESCHWISTER]: () => this.geschwisterEigenanteilKomponenteEnabled,
  };

  async ngOnInit(): Promise<void> {
    this.steps = WIZARD_STEPS.filter(step => (this.toggleableSteps[step.id]?.() ?? true));

    const stored = await this.storageService.getAllStepData(this.ticket.id);
    this.nameStepData = (stored[WizardStepId.NAME] as NameStepData) ?? this.buildNameBaseline();
    this.addressStepData = (stored[WizardStepId.ADDRESS] as AddressStepData) ?? this.buildAddressBaseline();
    this.emailStepData = (stored[WizardStepId.EMAIL] as EmailStepData) ?? this.buildEmailBaseline();
    this.schoolStepData = (stored[WizardStepId.SCHOOL] as SchoolDataInfoDto) ?? this.buildSchoolBaseline();
    this.geschwisterStepData = (stored[WizardStepId.GESCHWISTER] as GeschwisterStepData) ?? this.buildGeschwisterBaseline();
    this.paymentStepData = (stored[WizardStepId.PAYMENT] as StudentTicketMandateHolderDto) ?? this.ticket.mandateHolder;
    this.dataLoaded = true;
  }

  get visibleStepIds(): string[] {
    return this.steps.map(step => step.id);
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentStepIndex];
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get nextDisabled(): boolean {
    return !this.stepRef?.isValid();
  }

  // Steps, deren Ausgangswerte aus den aktuellen Ticketdaten ableitbar sind. Die Zuordnung
  // zum Request-Feld hält Vergleich und Batch-Aufbau an einer Stelle zusammen.
  private readonly diffableSteps: readonly DiffableStep[] = [
    this.createDiffableStep(WizardStepId.NAME, 'nameData', () => this.buildNameBaseline()),
    this.createDiffableStep(WizardStepId.ADDRESS, 'addressData', () => this.buildAddressBaseline()),
    this.createDiffableStep(WizardStepId.EMAIL, 'emailData', () => this.buildEmailBaseline()),
    this.createDiffableStep(WizardStepId.SCHOOL, 'schoolData', () => this.buildSchoolBaseline()),
    this.createDiffableStep(WizardStepId.GESCHWISTER, 'paymentData', () => this.buildGeschwisterBaseline()),
    this.createDiffableStep(WizardStepId.PAYMENT, 'mandateHolderData', () => this.ticket.mandateHolder),
  ];

  // Ordnet einer Step-Id die passende lokale Feld-Zuweisung zu, damit persistCurrentStep()
  // generisch bleibt und bei neuen Steps nur hier ein Eintrag ergänzt werden muss.
  private readonly stepDataSetters: Readonly<Partial<Record<WizardStepId, (value: unknown) => void>>> = {
    name: value => this.nameStepData = value as NameStepData,
    address: value => this.addressStepData = value as AddressStepData,
    email: value => this.emailStepData = value as EmailStepData,
    school: value => this.schoolStepData = value as SchoolDataInfoDto,
    geschwister: value => this.geschwisterStepData = value as GeschwisterStepData,
    payment: value => this.paymentStepData = value as StudentTicketMandateHolderDto,
  };

  private buildNameBaseline(): NameStepData {
    return {
      firstName: this.ticket.firstname,
      lastName: this.ticket.lastname,
    };
  }

  private buildAddressBaseline(): AddressStepData {
    return {
      street: this.ticket.addressData?.street ?? '',
      addressAddition: this.ticket.addressData?.addressAddition ?? '',
      postalCode: this.ticket.addressData?.postalCode ?? '',
      city: this.ticket.addressData?.city ?? '',
      country: this.ticket.addressData?.country ?? null,
    };
  }

  private buildEmailBaseline(): EmailStepData {
    return {
      email: this.ticket.contactData?.email ?? '',
    };
  }

  private buildSchoolBaseline(): SchoolDataInfoDto {
    return {
      schuleId: this.ticket.schuleId,
      schulTyp: this.ticket.schuleTyp,
      schuljahrId: this.ticket.schuljahrId,
      klasseId: this.ticket.klasseId,
      bildungsgangId: this.ticket.bildungsgangId,
      schuleName: this.ticket.schuleName,
      schuljahrName: this.ticket.schuljahrName,
      klasseName: this.ticket.klasseName,
      bildungsgangName: this.ticket.bildungsgangName,
    };
  }

  private buildGeschwisterBaseline(): GeschwisterStepData {
    return {
      geschwisterkind1Id: this.ticket.geschwisterkind1Id,
      geschwisterkind2Id: this.ticket.geschwisterkind2Id,
      geschwisterkind3Id: this.ticket.geschwisterkind3Id,
      geschwisterkind4Id: this.ticket.geschwisterkind4Id,
      geschwisterkind5Id: this.ticket.geschwisterkind5Id,
    };
  }

  async next(): Promise<void> {
    await this.persistCurrentStep();
    if (this.isLastStep) {
      await this.submit();
      return;
    }
    this.currentStepIndex++;
    this.maxReachedIndex = Math.max(this.maxReachedIndex, this.currentStepIndex);
  }

  async back(): Promise<void> {
    if (!this.isFirstStep) {
      await this.persistCurrentStep();
      this.currentStepIndex--;
    }
  }

  async goToStep(index: number): Promise<void> {
    if (index === this.currentStepIndex || index > this.maxReachedIndex) {
      return;
    }
    await this.persistCurrentStep();
    this.currentStepIndex = index;
  }

  cancel(): void {
    // Verworfene Zwischenstände dürfen nicht "hängen bleiben" und künftige Wizard-Aufrufe
    // mit veralteten Werten überschreiben (siehe storageService-Dokumentation).
    this.storageService.clearAll(this.ticket.id);
    this.activeModal.dismiss();
  }

  private async persistCurrentStep(): Promise<void> {
    if (this.currentStep.id === WizardStepId.SUMMARY || !this.stepRef) {
      return;
    }
    const value = this.stepRef.getValue();
    this.stepDataSetters[this.currentStep.id]?.(value);
    await this.storageService.saveStepData(this.ticket.id, this.currentStep.id, value);
  }

  private async submit(): Promise<void> {
    this.submitting = true;
    const batchUpdateRequest = (await this.hasActualChanges()) ? await this.buildBatchUpdateRequest() : null;
    const request$ = batchUpdateRequest
      ? this.studentTicketService.batchUpdate(this.ticket.id, batchUpdateRequest)
      : this.studentTicketService.verifyTicket(this.ticket.id);

    request$.subscribe({
      next: (verifiedAt) => {
        this.submitting = false;
        if (batchUpdateRequest) {
          this.applyBatchUpdateToTicket(batchUpdateRequest);
          this.customToastService.showSuccess(this.translateService.instant('TICKETS.UEBERPRUEFUNG_SUCCESS'));
        }
        this.ticket.verifiedAt = verifiedAt;
        this.storageService.clearAll(this.ticket.id);
        this.activeModal.close(verifiedAt);
      },
      error: () => {
        this.submitting = false;
        this.customToastService.showError(this.translateService.instant('TICKETS.UEBERPRUEFUNG_ERROR'));
      }
    });
  }

  // Generischer Feld-Merge: neue, gleichnamige Felder eines Abschnitts werden ohne Änderung an
  // dieser Stelle automatisch übernommen (siehe mergeInto). Nur wo Feldname (renameMap) oder
  // Semantik (postProcess) vom Ticket-Modell abweichen, braucht es hier einen Eintrag.
  private readonly ticketMergeConfig: readonly {
    field: keyof StudentTicketBatchUpdateRequestDto;
    target: (ticket: StudentTicketListViewAsmResponseDTO) => Record<string, unknown>;
    renameMap?: Record<string, string>;
    postProcess?: (ticket: StudentTicketListViewAsmResponseDTO, data: Record<string, unknown>) => void;
  }[] = [
    {field: 'nameData', target: t => t as unknown as Record<string, unknown>, renameMap: {firstName: 'firstname', lastName: 'lastname'}},
    {
      field: 'addressData',
      target: t => (t.addressData ??= {} as AddressDataInfoDto) as unknown as Record<string, unknown>,
      // addressAddition ist im Ticket-Modell pflicht - "nicht übermittelt" heißt hier "gelöscht", nicht "unverändert"
      postProcess: (t, data) => (t.addressData!.addressAddition = (data.addressAddition as string | null) ?? ''),
    },
    {field: 'emailData', target: t => (t.contactData ??= {} as StudentTicketContactDataDto) as unknown as Record<string, unknown>},
    {field: 'schoolData', target: t => t as unknown as Record<string, unknown>, renameMap: {schulTyp: 'schuleTyp'}},
    {
      field: 'paymentData',
      target: t => t as unknown as Record<string, unknown>,
      // Geschwisterkind-IDs können bewusst auf null gesetzt werden (Zuordnung entfernen) - der
      // generische Merge überspringt null/undefined, daher hier explizit alle 5 Felder übernehmen.
      postProcess: (t, data) => {
        const target = t as unknown as Record<string, unknown>;
        for (const key of ['geschwisterkind1Id', 'geschwisterkind2Id', 'geschwisterkind3Id', 'geschwisterkind4Id', 'geschwisterkind5Id']) {
          target[key] = data[key] ?? null;
        }
      },
    },
    {field: 'mandateHolderData', target: t => (t.mandateHolder ??= {} as StudentTicketMandateHolderDto) as unknown as Record<string, unknown>},
  ];

  // Spiegelt die übermittelten Werte direkt auf das Ticket-Objekt, damit ein erneutes Öffnen
  // des Wizards (ohne Reload) bereits die aktualisierten Daten als Ausgangsbasis zeigt.
  private applyBatchUpdateToTicket(request: StudentTicketBatchUpdateRequestDto): void {
    for (const {field, target, renameMap, postProcess} of this.ticketMergeConfig) {
      const data = request[field];
      if (!data) {
        continue;
      }
      const source = data as unknown as Record<string, unknown>;
      this.mergeInto(target(this.ticket), source, renameMap);
      postProcess?.(this.ticket, source);
    }
  }

  // Übernimmt alle vorhandenen (nicht null/undefined) Felder von source auf target - abweichende
  // Feldnamen werden über renameMap übersetzt. Neue, gleichnamige Felder benötigen dadurch keine
  // Änderung an ticketMergeConfig.
  private mergeInto(target: Record<string, unknown>, source: Record<string, unknown>, renameMap: Record<string, string> = {}): void {
    for (const [key, value] of Object.entries(source)) {
      if (value != null) {
        target[renameMap[key] ?? key] = value;
      }
    }
  }

  private async buildBatchUpdateRequest(): Promise<StudentTicketBatchUpdateRequestDto> {
    const ticketId = this.ticket.id;
    const request: StudentTicketBatchUpdateRequestDto = {};
    for (const step of this.diffableSteps) {
      await step.applyIfChanged(ticketId, request);
    }
    return request;
  }

  private createDiffableStep<Field extends keyof StudentTicketBatchUpdateRequestDto>(
    id: WizardStepId,
    field: Field,
    baseline: () => StudentTicketBatchUpdateRequestDto[Field],
  ): DiffableStep {
    return {
      id,
      field,
      applyIfChanged: async (ticketId, request) => {
        const stored = await this.storageService.getStepData<NonNullable<StudentTicketBatchUpdateRequestDto[Field]>>(ticketId, id);
        if (stored === null || this.isUnchangedStep(id, stored, baseline())) {
          return false;
        }
        request[field] = stored;
        return true;
      },
    };
  }

  private isUnchangedStep(
    id: WizardStepId,
    stored: object,
    baseline: object,
  ): boolean {
    if (id !== WizardStepId.PAYMENT
      || !this.isMandateHolder(stored)
      || !this.isMandateHolder(baseline)
      || !this.isProtectedIban(stored.mandateHolderIban)) {
      return isDeepEqual(stored, baseline);
    }

    const {mandateHolderIban: storedIban, ...storedWithoutIban} = stored;
    const {mandateHolderIban: baselineIban, ...baselineWithoutIban} = baseline;
    void storedIban;
    void baselineIban;
    return isDeepEqual(
      this.paymentDataForComparison(storedWithoutIban),
      this.paymentDataForComparison(baselineWithoutIban),
    );
  }

  private paymentDataForComparison(
    payment: Omit<StudentTicketMandateHolderDto, 'mandateHolderIban'> & {mandateHolderGender?: unknown},
  ): object {
    const {mandateHolderAddressAddition, mandateHolderGender, ...paymentData} = payment;
    void mandateHolderGender;
    return {
      ...paymentData,
      mandateHolderAddressAddition: mandateHolderAddressAddition ?? '',
    };
  }

  private isMandateHolder(value: unknown): value is StudentTicketMandateHolderDto {
    return typeof value === 'object' && value !== null && 'mandateHolderIban' in value;
  }

  private isProtectedIban(iban: string | null | undefined): boolean {
    return typeof iban === 'string' && (iban.startsWith('*') || iban.startsWith('enc:'));
  }

  // true, sobald ein Step vom Nutzer bearbeitet wurde und dabei tatsächlich von den
  // Ticket-Ausgangsdaten abweicht.
  private async hasActualChanges(): Promise<boolean> {
    const request = await this.buildBatchUpdateRequest();
    return Object.values(request).some(value => value != null);
  }
}
